"use client";

import type { NoteIndex } from "@common/entities/schema";
import { motion, AnimatePresence } from "framer-motion";
import { springSnappy, springSoft } from "./motion-tokens";
import { useCallback } from "react";
import { Play, Square } from "lucide-react";
import { useStores } from "@web/src/stores/web-stores";
import { keyDisplayName } from "@web/src/services/audio/drone-voicing";
import { DRONE_PRESETS, type DronePresetName } from "@web/src/services/audio/drone-presets";
import { observer } from "mobx-react";
import { showSilentModeToastOnce } from "@web/src/utils/silent-mode-toast";

const KEY_INDICES: NoteIndex[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const PRESET_NAMES: DronePresetName[] = ["celestial", "warm", "strings", "prayer"];

export const DronePadDemo = observer(function DronePadDemo() {
  const { droneStore } = useStores();

  const handleToggle = useCallback(async () => {
    if (!droneStore.isActive) showSilentModeToastOnce();
    await droneStore.toggle();
  }, [droneStore]);

  const handleKeyChange = useCallback(
    async (key: NoteIndex) => {
      await droneStore.setKey(key);
    },
    [droneStore],
  );

  const handlePresetChange = useCallback(
    async (preset: DronePresetName) => {
      await droneStore.setPreset(preset);
    },
    [droneStore],
  );

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="rounded-xl border border-border-primary bg-surface-secondary overflow-hidden shadow-2xl p-5">
        {/* Play/Stop */}
        <motion.button
          className="w-full py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2"
          style={{
            backgroundColor: droneStore.isActive ? "#FF6B6B" : "#C084FC",
            color: "white",
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={springSnappy}
          onClick={() => void handleToggle()}
        >
          {droneStore.isActive ? (
            <>
              <Square className="w-4 h-4" fill="currentColor" />
              Stop Drone
            </>
          ) : (
            <>
              <Play className="w-4 h-4" fill="currentColor" />
              Start Drone
            </>
          )}
        </motion.button>

        {/* Key grid */}
        <div className="mb-4 mt-5">
          <span className="text-[10px] font-medium text-glyphs-secondary uppercase tracking-wider">
            Key
          </span>
          <div className="grid grid-cols-4 gap-1.5 mt-2">
            {KEY_INDICES.map((idx) => (
              <motion.button
                key={idx}
                className="h-8 rounded-md text-xs font-medium border relative overflow-hidden"
                style={{
                  borderColor:
                    droneStore.currentKey === idx
                      ? "#C084FC"
                      : "var(--color-border-primary)",
                  color:
                    droneStore.currentKey === idx
                      ? "white"
                      : "var(--color-glyphs-primary)",
                }}
                onClick={() => void handleKeyChange(idx)}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.93 }}
                transition={springSnappy}
              >
                {droneStore.currentKey === idx && (
                  <motion.span
                    layoutId="drone-key-active"
                    className="absolute inset-0 rounded-md"
                    style={{ backgroundColor: "#C084FC" }}
                    transition={springSoft}
                  />
                )}
                <span className="relative z-10">{keyDisplayName(idx)}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Preset selector */}
        <div>
          <span className="text-[10px] font-medium text-glyphs-secondary uppercase tracking-wider">
            Preset
          </span>
          <div className="flex flex-col gap-1.5 mt-2">
            {PRESET_NAMES.map((name) => {
              const preset = DRONE_PRESETS[name];
              return (
                <motion.button
                  key={name}
                  className="h-auto py-2 px-3 rounded-md text-xs font-medium border relative overflow-hidden text-left"
                  style={{
                    borderColor: "var(--color-border-primary)",
                    color: "var(--color-glyphs-primary)",
                  }}
                  onClick={() => void handlePresetChange(name)}
                  whileTap={{ scale: 0.97 }}
                >
                  {droneStore.currentPreset === name && (
                    <motion.span
                      layoutId="drone-preset-active"
                      className="absolute inset-0 rounded-md bg-surface-tertiary"
                      transition={springSoft}
                    />
                  )}
                  <span className="relative z-10 flex flex-col">
                    <span className="font-medium">{preset.label}</span>
                    <span className="text-[10px] text-glyphs-secondary">
                      {preset.description}
                    </span>
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Visual indicator - fixed height to prevent layout shift */}
        <div className="mt-4 h-6 flex justify-center items-center">
          <AnimatePresence>
            {droneStore.isActive && (
              <motion.div
                className="flex gap-1 items-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="w-1 rounded-full"
                    style={{ backgroundColor: "#C084FC" }}
                    animate={{
                      height: [8, 20, 12, 24, 8],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.12,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
});
